import React from 'react';
import { connect } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import UnloadContainerScanPage, { ScanSidelinePage }
    from 'src/components/pages/unload-page/scan-container-page/UnloadContainerScanPage';
import { getFailureMessage, ORPHAN_ENDED_MESSAGE,
    ScanResultMessage } from 'src/components/pages/unload-page/common/UnloadScanStep';
import { AlertLevels } from 'src/models/alert-model';
import { CloseResult, DoorScanResult, LabelScanResult, Trailer } from 'src/models/unload';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import { buildLabelScanInput, LabelScanInput, scanLabel, scanSideline,
    UnloadPages } from 'src/store/common-slices/unloadSlice';
import { getBoundContainerId } from 'src/utils/unload-scan-results';

// The 2 s UX set for the loop to name the trailer just bound, unless a scan answers sooner
export const BOUND_NOTICE_MS = 2000;

// The door just accepted, naming the trailer at it
const getTrailerBoundMessage = (trailer: Trailer): ScanResultMessage => ({ type: AlertLevels.SUCCESS,
    title: AppStrings.UNLOAD_DOOR_ACCEPTED_PREFIX + trailer.doorId + AppStrings.UNLOAD_DOOR_ACCEPTED_SUFFIX,
    lines: [AppStrings.UNLOAD_SCANNING_TRAILER_PREFIX + trailer.name] });

// The pallet just accepted, or received as an orphan when it is on no trailer, by its label
const getPalletBoundMessage = (trailer: Trailer): ScanResultMessage => ({ type: AlertLevels.SUCCESS,
    title: trailer.trailerId ? AppStrings.UNLOAD_PALLET_ACCEPTED_TITLE : AppStrings.UNLOAD_RECEIVED_AS_ORPHAN_TITLE,
    lines: [AppStrings.UNLOAD_SCANNING_PALLET_PREFIX + trailer.palletId] });

interface Props {
    // What the loop scans into: the bound trailer, or in staging the open pallet; none in the orphan loop
    trailer?: Trailer;
    // Sideline's step is open over the loop
    sidelining: boolean;
    // The door or pallet scan that opened the loop, while it is the latest result
    lastScan?: DoorScanResult;
    // The latest label scanned in the loop, with its result
    lastLabel?: LabelScanResult;
    // A sideline ID not recorded, shown on the sideline step
    lastSideline?: LabelScanResult;
    // A close not done
    lastClose?: CloseResult;
    // End Orphan just returned to this loop, which says so until the next scan
    orphanEnded: boolean;
    // The associate's site, sent with each call
    warehouseId: string;
    // The associate's id, sent with each call
    associate: string;
    // The associate's locale, sent with each call
    locale: string;
    // Sends a label scanned in the loop
    scanLabel: (input: LabelScanInput) => void;
    // Sends a sideline ID
    scanSideline: (input: LabelScanInput) => void;
}

// The slice takes one scan at a time, ends a closed trailer and sounds every answer
export class UnloadContainerScanPageContainer extends React.Component<Props, { showBoundNotice: boolean }> {
    state = { showBoundNotice: true };
    private timer?: ReturnType<typeof setTimeout>;

    public componentDidMount() {
        this.timer = setTimeout(() => this.setState({ showBoundNotice: false }), BOUND_NOTICE_MS);
    }

    public componentWillUnmount() {
        clearTimeout(this.timer);
    }

    // A label, or a sideline ID marked as one. With no parent it is received as an orphan; in staging the parent is
    // the open pallet
    private buildInput = (containerId: string, isSideline?: boolean) => buildLabelScanInput(containerId,
        this.props.trailer && getBoundContainerId(this.props.trailer), this.props.warehouseId, this.props.associate,
        this.props.locale, isSideline);

    private onSubmit = (label: string) => this.props.scanLabel(this.buildInput(label));

    private onSideline = (id: string) => this.props.scanSideline(this.buildInput(id, true));

    public render() {
        const { trailer, lastScan, lastLabel, lastClose, orphanEnded } = this.props;
        if (this.props.sidelining) {
            return <ScanSidelinePage lastSideline={this.props.lastSideline} onSubmit={this.onSideline}/>;
        }
        const pallet = trailer?.palletId;
        // A pallet is named over its trailer, an orphan pallet alone, and a trailer over its door
        const heading = !trailer ? AppStrings.UNLOAD_ORPHAN_HEADING
            : (pallet ? [pallet, trailer.trailerId] : [trailer.name, trailer.doorId]).filter(Boolean)
                .join(AppStrings.UNLOAD_SEPARATOR);
        // Compared against undefined, since an empty trailer counts 0; the orphan loop has no count
        const count = trailer?.count === undefined ? undefined : trailer.count + AppStrings.UNLOAD_COUNT_SUFFIX;
        // A close not done shows here until the next scan
        const notice = lastClose ? getFailureMessage(lastClose) : orphanEnded ? ORPHAN_ENDED_MESSAGE
            : trailer && this.state.showBoundNotice && lastScan?.trailer
                ? (pallet ? getPalletBoundMessage : getTrailerBoundMessage)(trailer) : undefined;
        const chain = [trailer?.name ?? trailer?.trailerId, pallet].filter((id): id is string => id !== undefined);
        return <UnloadContainerScanPage heading={heading} count={count} chain={chain} cartons={pallet !== undefined}
                                        lastLabel={lastLabel} notice={notice} orphan={!trailer}
                                        onSubmit={this.onSubmit}/>;
    }
}

const mapStateToProps = (state: RootState) => ({
    // The orphan loop shares this step and has no trailer
    trailer: state.Unload.pageToRender === UnloadPages.SCAN_ORPHAN_PAGE ? undefined : state.Unload.trailer,
    sidelining: state.Unload.pageToRender === UnloadPages.SCAN_SIDELINE_PAGE,
    lastScan: state.Unload.lastScan,
    lastLabel: state.Unload.lastLabel,
    lastSideline: state.Unload.lastSideline,
    lastClose: state.Unload.lastClose,
    orphanEnded: state.Unload.orphanEnded,
    warehouseId: state.Init.initData.gradingNodeDetails.id,
    associate: state.Init.initData.associateDetails.id,
    locale: state.Init.initData.associateDetails.locales[0]
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
    scanLabel: (input: LabelScanInput) => dispatch(scanLabel(input)),
    scanSideline: (input: LabelScanInput) => dispatch(scanSideline(input))
});

export default connect(mapStateToProps, mapDispatchToProps)(UnloadContainerScanPageContainer);
